import type { ProductListingQuery } from '@akademi/site-builder-runtime';

/**
 * Shared URL <-> {@link ProductListingQuery} mapping for {@code ProductGrid}
 * and {@code FacetPanel}. Both blocks read the SAME URL state independently
 * — "the URL is the source of truth" — so an operator can drop a facet
 * sidebar next to a grid on one page with zero prop wiring between them,
 * exactly the pattern {@code CourseGrid} + {@code CategoryTree} already use
 * in this package. Keeping the mapping in one file means the two blocks
 * can't drift on param names.
 *
 * <h3>URL param scheme</h3>
 * <table>
 *   <tr><td>{@code ?q=}</td><td>{@link ProductListingQuery.q}</td></tr>
 *   <tr><td>{@code ?sort=}</td><td>{@link ProductListingQuery.sort}</td></tr>
 *   <tr><td>{@code ?page=}</td><td>{@link ProductListingQuery.page}</td></tr>
 *   <tr><td>{@code ?inStock=true}</td><td>{@link ProductListingQuery.inStockOnly}</td></tr>
 *   <tr><td>{@code ?minPrice=} / {@code ?maxPrice=}</td>
 *       <td>verbatim decimal strings — never parsed, matches
 *       {@link MoneyValue.amount}'s own rule.</td></tr>
 *   <tr><td>{@code ?f_<code>=v1,v2}</td>
 *       <td>{@link ProductListingQuery.attributes}{@code [code]},
 *       csv-encoded — mirrors CourseGrid's {@code ?level=} / {@code ?ct=}.</td></tr>
 * </table>
 *
 * <p>{@code categorySlugPath} / {@code brandSlug} / {@code productType} /
 * {@code featured} are NOT read from the URL — they're the operator-set
 * "scope" props on each block (which category/brand page this instance is
 * pinned to), passed in as {@link ProductQueryScope}.
 */
export interface ProductQueryScope {
  categorySlugPath?: string;
  brandSlug?: string;
  productType?: string;
  featured?: boolean;
}

const FACET_PREFIX = 'f_';

function splitCsv(raw: string | null | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Build the effective query from URL params + the block's static scope. */
export function readProductQuery(
  searchParams: URLSearchParams | null | undefined,
  scope: ProductQueryScope = {},
): ProductListingQuery {
  const attributes: Record<string, string[]> = {};
  if (searchParams) {
    for (const key of searchParams.keys()) {
      if (!key.startsWith(FACET_PREFIX)) continue;
      const values = splitCsv(searchParams.get(key));
      if (values.length > 0) attributes[key.slice(FACET_PREFIX.length)] = values;
    }
  }
  // The URL is 1-based (`?page=1` is the first page, matching CourseGrid and
  // BlogArticleGrid and what a reader expects to see in a link). The API is
  // 0-based -- the backend uses `page * size` as its offset. Converting here,
  // once, is the whole reason this helper exists: passing the URL number
  // straight through made `?page=1` skip the first `size` products and left
  // the real first page unreachable from any pagination control.
  const pageRaw = searchParams?.get('page');
  const humanPage = pageRaw ? Number(pageRaw) : undefined;
  const page = humanPage && Number.isFinite(humanPage) && humanPage > 0 ? humanPage - 1 : undefined;
  const minPrice = searchParams?.get('minPrice') || undefined;
  const maxPrice = searchParams?.get('maxPrice') || undefined;

  return {
    ...scope,
    q: searchParams?.get('q') || undefined,
    sort: searchParams?.get('sort') || undefined,
    page,
    inStockOnly: searchParams?.get('inStock') === 'true' || undefined,
    minPrice,
    maxPrice,
    attributes: Object.keys(attributes).length > 0 ? attributes : undefined,
  };
}

/**
 * Merge `patch` into the current params. Any key set to `undefined` or `''`
 * is removed. Unless the patch itself is a page change, `page` is dropped —
 * a facet/search/sort change carried under a stale page number can land past
 * the end of the new result set.
 */
export function patchProductParams(
  current: URLSearchParams | null | undefined,
  patch: Record<string, string | undefined>,
): URLSearchParams {
  const params = new URLSearchParams(current?.toString() ?? '');
  for (const [key, value] of Object.entries(patch)) {
    if (value == null || value === '') params.delete(key);
    else params.set(key, value);
  }
  if (!('page' in patch)) params.delete('page');
  return params;
}

/** Toggle one facet value on/off for `code` and return the resulting params
 *  (resets `page`, same reasoning as {@link patchProductParams}). */
export function toggleFacetValue(
  current: URLSearchParams | null | undefined,
  code: string,
  value: string,
): URLSearchParams {
  const params = new URLSearchParams(current?.toString() ?? '');
  const key = `${FACET_PREFIX}${code}`;
  const existing = splitCsv(params.get(key));
  const next = existing.includes(value)
    ? existing.filter((v) => v !== value)
    : [...existing, value];
  if (next.length > 0) params.set(key, next.join(','));
  else params.delete(key);
  params.delete('page');
  return params;
}

/** Whether facet code/value is currently selected, per the URL. */
export function isFacetValueSelected(
  searchParams: URLSearchParams | null | undefined,
  code: string,
  value: string,
): boolean {
  return splitCsv(searchParams?.get(`${FACET_PREFIX}${code}`)).includes(value);
}

/** True when at least one filter (facet, search, price, stock) beyond the
 *  block's static scope is active — drives a "Clear all" affordance. */
export function hasActiveProductFilters(searchParams: URLSearchParams | null | undefined): boolean {
  if (!searchParams) return false;
  for (const key of searchParams.keys()) {
    if (key.startsWith(FACET_PREFIX)) return true;
  }
  return (
    Boolean(searchParams.get('q')) ||
    Boolean(searchParams.get('minPrice')) ||
    Boolean(searchParams.get('maxPrice')) ||
    searchParams.get('inStock') === 'true'
  );
}

/** URL with every facet/search/price/stock filter removed (sort + scope
 *  props are untouched — sort isn't a "filter" and scope isn't URL state). */
export function clearProductParams(current: URLSearchParams | null | undefined): URLSearchParams {
  const params = new URLSearchParams(current?.toString() ?? '');
  for (const key of Array.from(params.keys())) {
    if (key.startsWith(FACET_PREFIX)) params.delete(key);
  }
  params.delete('q');
  params.delete('minPrice');
  params.delete('maxPrice');
  params.delete('inStock');
  params.delete('page');
  return params;
}
